import { ApiEndpoints } from "@shared/api/api-endpoints";
import { bxApiClient } from "@shared/api/clients/apiClientFactory";
import { QueryStringParameters } from "@shared/api/url-builder";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { bxPrintingDistributionUtils } from "./utils";
// export const getBillTemplates = async (): Promise<OptionData[]> => {

export const PrintingDistributionApi = {
  getDistribution: async (queryParams: Record<string, any>): Promise<any> => {
    const utilityId = getRemoteUtilityId();
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "bx",
      "new-distribution",
      (query) => {
        query.push("remote_utility_id", utilityId);
        Object.entries(queryParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            query.push(key, String(value));
          }
        });
      }
    );
    const response = await bxApiClient.get(url);
    return response.data;
  },

  exportDistribution: async (
    queryParams: Record<string, any>
  ): Promise<any> => {
    const utilityId = getRemoteUtilityId();
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "bx",
      "new-distribution",
      (query) => {
        query.push("remote_utility_id", utilityId);
        Object.entries(queryParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            query.push(key, String(value));
          }
        });
        query.push("export_format", "csv");
      }
    );

    const response = await bxApiClient.get(url);

    return response.data;
  },

  getPrintData: async (queryParams: Record<string, any>): Promise<any> => {
    const utilityId = getRemoteUtilityId();
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "bx",
      "new-print-data",
      (query) => {
        query.push("remote_utility_id", utilityId);
        Object.entries(queryParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            query.push(key, String(value));
          }
        });
      }
    );
    const response = await bxApiClient.get(url);
    return bxPrintingDistributionUtils.convertToSnakeCase(response.data);
  },

  exportPrintData: async (queryParams: Record<string, any>): Promise<any> => {
    const utilityId = getRemoteUtilityId();
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "bx",
      "new-print-data",
      (query) => {
        query.push("remote_utility_id", utilityId);
        Object.entries(queryParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            query.push(key, String(value));
          }
        });
        query.push("export_format", "csv");
      }
    );

    const response = await bxApiClient.get(url);

    return response.data;
  },

  // Get print summary
  getPrintSummary: async (utilityId: number): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "bx",
      "new-print-summary",
      (query) => {
        query.push("remote_utility_id", utilityId);
      }
    );
    const response = await bxApiClient.get(url);
    return response.data.result;
  },

  // Get distribution summary
  getDistributionSummary: async (utilityId: string | number): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "bx",
      "new-distribution-summary",
      (query) => {
        query.push("remote_utility_id", utilityId);
      }
    );
    const response = await bxApiClient.get(url);
    return response.data.result;
  },

  getConsumerList: async (params: Record<string, any>): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "bx",
      "new-distribution-consumer-list",
      (query) => {
        Object.entries(params).forEach(([key, value]) => {
          if (
            value !== undefined &&
            value !== null &&
            value !== "" &&
            !(Array.isArray(value) && value.length === 0)
          ) {
            // If the value is an array, push each value with the same key
            if (Array.isArray(value)) {
              value.forEach((v) => {
                query.push(key, String(v));
              });
            } else {
              query.push(key, String(value));
            }
          }
        });
      }
    );

    const response = await bxApiClient.get(url);
    return response.data;
  },

  getConsumerListSummary: async (billRunId: string | number): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "bx",
      "new-distribution-consumer-list-summary",
      (query) => {
        query.push("bill_run_id", billRunId.toString());
      }
    );
    const response = await bxApiClient.get(url);
    return response.data.result;
  },

  getPaperBillDistribution: async (
    billRunId: string | number
  ): Promise<any> => {
    const utilityId = getRemoteUtilityId();

    const url = ApiEndpoints.createUrlWithQueryParameters(
      "bx",
      `new-paper-bill-distribution/${billRunId}`,
      (query) => {
        query.push("remote_utility_id", utilityId);
      }
    );
    const response = await bxApiClient.get(url);
    return response.data;
  },

  getPaperBillDistributionSummary: async (
    billRunId: string | number
  ): Promise<any> => {
    const utilityId = getRemoteUtilityId();

    const url = ApiEndpoints.createUrlWithQueryParameters(
      "bx",
      `new-paper-bill-distribution-summary/${billRunId}`,
      (query) => {
        query.push("remote_utility_id", utilityId);
      }
    );
    const response = await bxApiClient.get(url);
    return response.data.result;
  },

  updatePrintStatus: async (billRunId: number, payload: any): Promise<any> => {
    const utilityId = getRemoteUtilityId();
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "bx",
      `new-print-data/${billRunId}`,
      (query) => {
        query.push("remote_utility_id", utilityId);
      }
    );
    const response = await bxApiClient.put(url, payload);
    return response.data;
  },
};

export const downloadBillTemplate = async (billId: string): Promise<Blob> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `render-dynamic-template/${billId}`
  );
  const response = await bxApiClient.get(url, {
    responseType: "blob",
  });
  return response.data;
};

export const initiateDownloadZip = async (
  runId: string | number
): Promise<Blob> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `download-all-bill/${runId}`
  );
  const response = await bxApiClient.get(url);
  return response.data;
};

export const getVendorList = async (): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `vendors/dropdown`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID.toLocaleString());
    }
  );
  const response = await bxApiClient.get(url);

  const data = response.data?.result;
  return data.map((item: any) => ({
    value: item.id,
    label: item.vendorName,
  }));
};
